'use client';

import { useState, useEffect, useRef } from 'react';
import { Stack, HStack, VStack } from '@terra/ui-styled-system';
import { LegacyDialog, Button, Text, Badge } from '@terra/ui-design-system';
import { useProxyStore } from '../../store/proxy-store';
import { TagInput } from './TagInput';
import { COMMON_TAGS } from './constants';

type TagsDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  requestId: string;
  currentTags?: string[];
};

export function TagsDialog({
  isOpen,
  onClose,
  requestId,
  currentTags = [],
}: TagsDialogProps) {
  const [tags, setTags] = useState<string[]>([]);
  const setRequestTags = useProxyStore((state) => state.setRequestTags);
  const currentTagsRef = useRef(currentTags);

  currentTagsRef.current = currentTags;

  useEffect(() => {
    if (isOpen) {
      setTags(currentTagsRef.current || []);
    }
  }, [isOpen]); // Seed from the latest tags only when the dialog opens

  const handleSave = () => {
    setRequestTags(requestId, tags);
    onClose();
  };

  const handleAddTag = (tag: string) => {
    const normalizedTag = tag.toLowerCase().trim();

    if (!tags.includes(normalizedTag)) {
      setTags([...tags, normalizedTag]);
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleQuickAdd = (tag: string) => {
    if (!tags.includes(tag)) {
      setTags([...tags, tag]);
    }
  };

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxWidth="500px">
          <Stack gap="6" p="6">
            {/* Title & Description */}
            <Stack gap="1">
              <LegacyDialog.Title>Request Tags</LegacyDialog.Title>
              <LegacyDialog.Description>
                Add tags to organize and filter requests
              </LegacyDialog.Description>
            </Stack>

            {/* Content */}
            <Stack gap="4">
              <TagInput
                tags={tags}
                onAddTag={handleAddTag}
                onRemoveTag={handleRemoveTag}
              />

              {/* Quick Add Common Tags */}
              <VStack gap="2" alignItems="stretch">
                <Text fontSize="xs" color="fg.muted">
                  Quick add:
                </Text>
                <HStack gap="1" flexWrap="wrap">
                  {COMMON_TAGS.filter((tag) => !tags.includes(tag)).map(
                    (tag) => (
                      <Badge
                        key={tag}
                        size="sm"
                        variant="emphasis"
                        cursor="pointer"
                        onClick={() => handleQuickAdd(tag)}
                        _hover={{ bg: 'bg.muted' }}
                      >
                        + {tag}
                      </Badge>
                    ),
                  )}
                </HStack>
              </VStack>
            </Stack>

            {/* Actions */}
            <HStack gap="2" justifyContent="flex-end">
              <Button variant="outline" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave}>
                Save
              </Button>
            </HStack>
          </Stack>

          <LegacyDialog.CloseTrigger />
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
